import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  completeWfSetup,
  fetchWfSetupOptions,
  fetchWfSetupStatus,
  saveWfSetupProgress,
} from "../../../Moduels/attendance/wfApi";
import { ATTENDANCE_CONFIGURATION_PATH } from "./attendanceSetupGate";
import { useSetupProgress } from "../../../Moduels/Context/SetupProgressContext";
import "./wf-attendance-setup.css";

function formatApiError(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) =>
        typeof item === "string" ? item : item?.msg || JSON.stringify(item)
      )
      .join("; ");
  }
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return fallback;
}

const STEPS = [
  { n: 1, title: "Attendance method", key: "method" },
  { n: 2, title: "Review & activate", key: "review" },
];

const SOURCE_LABELS = {
  default_present: "Default Present",
  manual_hr: "Manual Entry",
  biometric: "Biometric",
  browser_login: "Website Login Entry",
  excel_upload: "Excel Upload",
};

export default function WfAttendanceSetup() {
  const navigate = useNavigate();
  const setupProgressCtx = useSetupProgress();
  const [step, setStep] = useState(1);
  const [options, setOptions] = useState(null);
  const [selectedSource, setSelectedSource] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [status, opts] = await Promise.all([
          fetchWfSetupStatus(),
          fetchWfSetupOptions(),
        ]);
        setOptions(opts);

        if (!status.setup_required && status.setup_completed_at) {
          navigate(ATTENDANCE_CONFIGURATION_PATH, { replace: true });
          return;
        }

        const savedSources = status?.setup_progress?.step_1?.sources;
        if (Array.isArray(savedSources) && savedSources.length === 1) {
          setSelectedSource(savedSources[0]);
        }
        if (status.current_step && status.current_step <= 2) {
          setStep(Math.min(2, status.current_step));
        }
      } catch (e) {
        setErr(formatApiError(e, "Failed to load setup"));
      } finally {
        setChecking(false);
      }
    })();
  }, [navigate]);

  const persist = async (nextStep, payload) => {
    await saveWfSetupProgress(nextStep, payload);
  };

  const next = async () => {
    setErr("");
    setBusy(true);
    try {
      if (step === 1) {
        if (!selectedSource) throw new Error("Select an attendance method");
        await persist(1, { sources: [selectedSource] });
        setStep(2);
      } else if (step === 2) {
        await completeWfSetup({
          sources: [selectedSource],
          industry: "custom",
          cycle_type: "five_day",
          cycle_config: {},
          behaviors: [],
        });
        if (setupProgressCtx?.refetch) await setupProgressCtx.refetch();
        navigate(`${ATTENDANCE_CONFIGURATION_PATH}?welcome=1`, { replace: true });
      }
    } catch (e) {
      setErr(formatApiError(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  const back = () => setStep(1);

  if (checking || !options) {
    return <div className="wf-setup-loading">Loading attendance setup…</div>;
  }

  const sourceOptions = options?.sources || [];
  const selectedLabel = SOURCE_LABELS[selectedSource] || selectedSource;

  return (
    <div className="wf-setup-root">
      <div className="wf-setup-hero">
        <h1>Organisation attendance setup</h1>
        <p>
          Choose how your organisation captures attendance. Only one method is active at a time.
          Complete this once — you will not be asked again.
        </p>
      </div>

      <div className="wf-setup-steps">
        {STEPS.map((s) => (
          <span
            key={s.n}
            className={`wf-setup-step ${step === s.n ? "wf-setup-step--active" : step > s.n ? "wf-setup-step--done" : ""}`}
          >
            {s.n}. {s.title}
          </span>
        ))}
      </div>

      {err && <div className="wf-setup-err">{err}</div>}

      <div className="wf-setup-card">
        <div className="wf-setup-card-body">
          {step === 1 && (
            <>
              <p className="wf-setup-card-title">How will you take attendance?</p>
              <p className="wf-setup-card-sub">Select one method. Other options will remain disabled.</p>
              <div className="wf-setup-methods">
                {sourceOptions.map((o) => (
                  <label
                    key={o.code}
                    className={`wf-setup-method ${selectedSource === o.code ? "wf-setup-method--selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="attendance_source"
                      checked={selectedSource === o.code}
                      onChange={() => setSelectedSource(o.code)}
                    />
                    <div className="wf-setup-method-text">
                      <strong>{o.label}</strong>
                      <span>{o.description || ""}</span>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p className="wf-setup-card-title">Review your selection</p>
              <p className="wf-setup-card-sub">Confirm and activate your attendance method.</p>
              <dl className="wf-setup-review">
                <div className="wf-setup-review-row">
                  <dt>Attendance method</dt>
                  <dd>{selectedLabel || "—"}</dd>
                </div>
                <div className="wf-setup-review-row">
                  <dt>Work week</dt>
                  <dd>Mon – Fri (default)</dd>
                </div>
                <div className="wf-setup-review-row">
                  <dt>Industry template</dt>
                  <dd>Custom (default)</dd>
                </div>
              </dl>
            </>
          )}
        </div>
      </div>

      <div className="wf-setup-actions">
        {step > 1 && (
          <button type="button" onClick={back} disabled={busy} className="wf-setup-btn-back">
            Back
          </button>
        )}
        <button
          type="button"
          onClick={next}
          disabled={busy || (step === 1 && !selectedSource)}
          className="wf-setup-btn-primary"
        >
          {step === 2 ? (busy ? "Activating…" : "Complete setup") : busy ? "Saving…" : "Continue"}
        </button>
      </div>
    </div>
  );
}
